import { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useGetSquaresGameRulesVersionsQuery } from '@/api/squares/getSquaresGameRulesVersions';
import { useUpdateSquaresGameRulesVersionMutation } from '@/api/squares/updateSquaresGameRulesVersion';
import { SquareFormValues } from '@/utils/forms/squareTransformers';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

interface GameRulesSectionProps {
  disabled?: boolean;
}

export function GameRulesSection({ disabled = false }: GameRulesSectionProps) {
  const { values, setFieldValue } = useFormikContext<SquareFormValues>();
  const { data: versionsData, refetch } = useGetSquaresGameRulesVersionsQuery();
  const versions = versionsData?.rules_versions || [];
  const defaultVersion = versions.find((v: any) => v.is_default ?? v.isDefault);

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    values.game_rules_version_id || defaultVersion?.id || ''
  );
  const [editingRulesText, setEditingRulesText] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  const updateMutation = useUpdateSquaresGameRulesVersionMutation();

  // Helper: API may return camelCase (Drizzle) or snake_case; support both
  const getRulesText = (v: any) => (v?.rulesText ?? v?.rules_text ?? '') as string;

  // Initialize selected version and rules text
  useEffect(() => {
    const versionId = values.game_rules_version_id || defaultVersion?.id || '';
    setSelectedVersionId(versionId);
    const selectedVersion = versions.find((v: any) => v.id === versionId);
    if (selectedVersion) {
      setEditingRulesText(getRulesText(selectedVersion));
      setFieldValue('game_rules_version_id', versionId, false);
    }
  }, [versions, defaultVersion, values.game_rules_version_id, setFieldValue]);

  const selectedVersion = versions.find((v: any) => v.id === selectedVersionId);

  const handleVersionChange = (versionId: string) => {
    setSelectedVersionId(versionId);
    setFieldValue('game_rules_version_id', versionId);
    const version = versions.find((v: any) => v.id === versionId);
    if (version) {
      setEditingRulesText(getRulesText(version));
      setIsEditing(false);
    }
  };

  const handleStartEditing = () => {
    if (selectedVersion) {
      setEditingRulesText(getRulesText(selectedVersion));
      setIsEditing(true);
    }
  };

  const handleSaveRules = async () => {
    if (!selectedVersionId || !editingRulesText.trim()) {
      toast.error('Rules text cannot be empty');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: selectedVersionId,
        rules_text: editingRulesText.trim(),
      });
      toast.success('Rules updated successfully');
      setIsEditing(false);
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update rules');
    }
  };

  const handleCancelEditing = () => {
    if (selectedVersion) {
      setEditingRulesText(getRulesText(selectedVersion));
      setIsEditing(false);
    }
  };

  if (versions.length === 0) {
    return (
      <div className="space-y-4 border-t pt-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Game Rules</h3>
          <p className="text-sm text-muted-foreground">
            No game rules versions available. Please create one in the settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 border-t pt-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Game Rules</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Select a rules version for this contest. You can update the rules text for the selected
          version.
        </p>
      </div>

      <div className="space-y-4">
        {/* Rules Version Selector */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="game_rules_version_id">Rules Version</Label>
          <Select value={selectedVersionId} onValueChange={handleVersionChange} disabled={disabled}>
            <SelectTrigger id="game_rules_version_id">
              <SelectValue placeholder="Select a rules version" />
            </SelectTrigger>
            <SelectContent>
              {versions.map((v: any) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.title} {(v.is_default ?? v.isDefault) && '(Default)'}{' '}
                  {v.version && `- ${v.version}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Rules Text Display/Editor */}
        {selectedVersion && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="rules-text">Rules Text</Label>
              {!isEditing && !disabled && (
                <Button type="button" variant="outline" size="sm" onClick={handleStartEditing}>
                  Edit Rules
                </Button>
              )}
            </div>
            {isEditing ? (
              <div className="space-y-2">
                <RichTextEditor
                  content={editingRulesText ?? ''}
                  onChange={setEditingRulesText}
                  disabled={updateMutation.isPending}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleSaveRules}
                    disabled={updateMutation.isPending || !editingRulesText.trim()}
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {updateMutation.isPending ? 'Saving...' : 'Save Rules'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEditing}
                    disabled={updateMutation.isPending}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="border rounded-lg p-4 bg-muted/50 prose prose-invert prose-sm max-w-none break-words overflow-wrap-anywhere"
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: getRulesText(selectedVersion) }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
